// Convert "uniqueStrings" -> { [str]: "hsl(...)" }
// Colors are distributed to be as different as possible within this set.
export function generateColorSet(uniqueStrings, opts = {}) {
    const {
        rotate = 0,                 // degrees to rotate the whole palette
        saturations = [70, 60, 80], // cycle to keep neighbors distinct
        lightnesses = [52, 62, 42], // cycle to keep neighbors distinct
        format = "hsl",             // "hsl" | "hex"
    } = opts;

    // 1) Dedupe + stable sort for deterministic mapping
    const items = Array.from(new Set(uniqueStrings)).sort((a, b) =>
        String(a).localeCompare(String(b))
    );
    const n = items.length;

    // 2) Build evenly spaced palette using Van der Corput (base 2)
    const colors = new Array(n);
    for (let i = 0; i < n; i++) {
        const u = vdc(i + 1, 2); // (0,1]
        const hue = (u * 360 + rotate) % 360;
        const s = saturations[i % saturations.length];
        const l = lightnesses[i % lightnesses.length];
        colors[i] = format === "hex" ? hslToHex(hue, s, l) : `hsl(${hue.toFixed(1)} ${s}% ${l}%)`;
    }

    // 3) Map back to strings
    const map = {};
    items.forEach((str, i) => (map[str] = colors[i]));
    return map;
}

// Van der Corput radical-inverse (base b)
// Produces a low-discrepancy sequence in (0,1] for nice hue spacing.
function vdc(n, base = 2) {
    let v = 0;
    let denom = 1;
    while (n > 0) {
        denom *= base;
        v += (n % base) / denom;
        n = Math.floor(n / base);
    }
    return v;
}

// Optional: HSL -> HEX if you prefer hex output
function hslToHex(h, s, l) {
    // h in [0,360), s,l in [0,100]
    s /= 100; l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const hp = h / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    let [r1, g1, b1] = [0, 0, 0];
    if (0 <= hp && hp < 1) [r1, g1, b1] = [c, x, 0];
    else if (1 <= hp && hp < 2) [r1, g1, b1] = [x, c, 0];
    else if (2 <= hp && hp < 3) [r1, g1, b1] = [0, c, x];
    else if (3 <= hp && hp < 4) [r1, g1, b1] = [0, x, c];
    else if (4 <= hp && hp < 5) [r1, g1, b1] = [x, 0, c];
    else if (5 <= hp && hp < 6) [r1, g1, b1] = [c, 0, x];
    const m = l - c / 2;
    const r = Math.round((r1 + m) * 255);
    const g = Math.round((g1 + m) * 255);
    const b = Math.round((b1 + m) * 255);
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function toHex(v) {
    const s = v.toString(16);
    return s.length === 1 ? "0" + s : s;
}
